import { Select } from 'antd';
import styles from './dropdown.module.scss';
import Icon from '@ant-design/icons/lib/components/Icon';
import ChevronDown from '@/assets/icons/chevron-down.svg?react';
import ChevronUp from '@/assets/icons/chevron-up.svg?react';
import { SelectProps } from 'antd/es/select';
import { ReactNode, useState } from 'react';
import { FieldStatus } from '../form.types';
import clsx from 'clsx';
import { useScreenSize } from '@/hooks/use-screen-size/use-screen-size';

type DropdownProps = Omit<SelectProps, 'status' | 'open' | 'onOpenChange'> & {
  status?: FieldStatus;
  prefixIcon?: ReactNode;
  dropdownListClass?: string;
  wrapperClassName?: string;
  allowClear?: boolean;
} & (
    | { open: boolean; onOpenChange: (open: boolean) => void }
    | { open?: undefined; onOpenChange?: (open: boolean) => void }
  );

export default function Dropdown({
  options,
  status,
  disabled,
  open,
  onOpenChange,
  prefixIcon,
  dropdownListClass,
  wrapperClassName,
  allowClear = true,
  ...restProps
}: DropdownProps) {
  // `open` is honoured on every render, not seeded once: a parent that flips it
  // after mount owns the popup. Local state only drives the uncontrolled case.
  // Passing `open` therefore means taking full ownership, so `onOpenChange` is
  // required alongside it or the popup can never move.
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : uncontrolledOpen;
  const screenSize = useScreenSize();
  const isMobile = screenSize.width < 768;

  const changeOpen = (next: boolean) => {
    if (!isControlled) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };

  return (
    <div className={clsx(wrapperClassName, styles.dropdownWrapper)}>
      {prefixIcon && <span className={styles.dropdownPrefixIcon}>{prefixIcon}</span>}
      <Select
        {...restProps}
        allowClear={allowClear}
        open={isOpen}
        onOpenChange={changeOpen}
        disabled={disabled}
        classNames={{
          popup: {
            root: clsx(styles.dropdownList, dropdownListClass && dropdownListClass),
          },
        }}
        rootClassName={clsx(
          styles.dropdown,
          disabled && styles['dropdown-disabled'],
          isOpen && styles.dropdownOpen,
          status && styles[`dropdown-${status}`],
          prefixIcon && styles.dropdownPaddingLeft
        )}
        options={options}
        variant="borderless"
        tabIndex={0}
        suffixIcon={
          isMobile ? undefined : (
            <Icon
              onClick={() => changeOpen(!isOpen)}
              style={{ cursor: 'unset' }}
              className={clsx(styles.chevronIcon, disabled && styles.chevronIconDisabled)}
              component={() => (isOpen ? <ChevronUp /> : <ChevronDown />)}
            />
          )
        }
      />
    </div>
  );
}
